import { useCallback, useEffect, useRef, useState } from 'react';
import { Copy, Download, Loader2, Pencil, RefreshCw, Save } from 'lucide-react';
import {
  spaceErrorMessage,
  spacesApi,
  type SpaceFileDetail,
  type SpaceSummary,
  type SpaceText,
  type SpaceUnpack,
} from '../../lib/api';
import { isConflict } from '../../lib/space-upload';
import { INDEX_STATUS_LABEL, downloadFile, formatBytes, previewKindOf, useRefreshWhileIndexing } from '../../lib/spaces';
import { cn, formatDate } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { BreathingLabel } from '../ui/breathing';
import { Button } from '../ui/button';
import { Drawer } from '../ui/drawer';
import { Textarea } from '../ui/input';
import { Markdown } from '../ui/markdown';

/** An archive's unpacking, in a line: the spaces worker unpacks every zip, 7z and tar uploaded, by itself. */
function unpackLabel(unpack: SpaceUnpack): string {
  const files = `${unpack.files.toLocaleString()} file${unpack.files === 1 ? '' : 's'}`;
  switch (unpack.status) {
    case 'QUEUED':
      return 'Waiting to be unpacked';
    case 'RUNNING':
      return `Unpacking… ${files} so far`;
    case 'DONE':
      return `${files}, into`;
    case 'STOPPED':
      return `Paused after ${files}; it carries on by itself`;
    case 'FAILED':
      return 'Could not be unpacked';
    case 'SUPERSEDED':
      return 'A newer version was unpacked instead';
  }
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 text-xs text-ink-secondary break-words">{children}</dd>
    </div>
  );
}

/**
 * What a file holds, shown in place.
 *
 * Text comes through `GET …/text` and is rendered here — markdown through the shared renderer, which
 * never lets raw HTML through, since these files are uploaded by people and agents and are not ours to
 * trust. An office document's text is what the spaces worker read out of it, through the same
 * endpoint, once it has. PDFs and images are shown from a signed inline URL on storage's own origin,
 * which the server grants only for types a browser cannot run script from.
 */
function Preview({
  file, text, textError, inlineUrl, inlineError, loading,
}: {
  file: SpaceFileDetail;
  text: SpaceText | null;
  textError: string | null;
  inlineUrl: string | null;
  inlineError: string | null;
  loading: boolean;
}) {
  const kind = previewKindOf(file.mimeType, file.name);
  if (file.status === 'PENDING') {
    return <p className="text-xs text-ink-faint">This upload has not finished, so there is nothing to show yet.</p>;
  }
  if (loading) return <p className="text-xs text-ink-faint">Loading preview…</p>;
  if (kind === 'extracted') {
    if (file.indexStatus === 'PENDING' || file.indexStatus === 'NONE') return <BreathingLabel label="Reading the text out of this file…" />;
    if (textError) return <p className="text-xs text-ink-muted">{textError}</p>;
    if (!text) return null;
    if (text.content.trim().length === 0) return <p className="text-xs text-ink-faint italic">No text was found in this file.</p>;
    return (
      <div className="space-y-2">
        <p className="text-[10px] text-ink-faint">
          The text read out of this file, which is what search matches.{text.truncated ? ' Only the beginning is shown; download the file for the rest.' : ''}
        </p>
        {text.markdown ? (
          <Markdown>{text.content}</Markdown>
        ) : (
          <pre className="text-[11px] font-mono text-ink-secondary whitespace-pre-wrap break-words bg-surface-subtle border border-border-subtle rounded-md p-3 max-h-[60vh] overflow-auto">
            {text.content}
          </pre>
        )}
      </div>
    );
  }
  if (kind === 'markdown' || kind === 'text') {
    if (textError) return <p className="text-xs text-ink-muted">{textError}</p>;
    if (!text) return null;
    if (text.content.length === 0) return <p className="text-xs text-ink-faint italic">This file is empty.</p>;
    return kind === 'markdown' ? (
      <Markdown>{text.content}</Markdown>
    ) : (
      <pre className="text-[11px] font-mono text-ink-secondary whitespace-pre-wrap break-words bg-surface-subtle border border-border-subtle rounded-md p-3 max-h-[60vh] overflow-auto">
        {text.content}
      </pre>
    );
  }
  if (kind === 'pdf' || kind === 'image') {
    if (inlineError) return <p className="text-xs text-ink-muted">{inlineError}</p>;
    if (!inlineUrl) return null;
    return kind === 'pdf' ? (
      <iframe src={inlineUrl} title={file.name} className="w-full h-[70vh] rounded-md border border-border bg-surface-subtle" />
    ) : (
      <img src={inlineUrl} alt={file.name} className="max-w-full rounded-md border border-border" />
    );
  }
  return <p className="text-xs text-ink-muted">No preview for this type yet. Download it to open it.</p>;
}

/**
 * A file — its actions, details, preview and versions — without a frame, so the Spaces page can show it
 * in a drawer and the Feed in its reading pane. Read-only unless `canEditIn` and `me` are given.
 */
export function FileDetail({
  fileId, canEditIn, me, onChanged, onCopyPath, onLoaded, onDirtyChange,
}: {
  fileId: string;
  /**
   * Whether the children of this folder may be edited — the listing's `can.edit`, which the page knows
   * only for the folder it has open. Asked with the file's own parent, so a file reached by a link from
   * elsewhere is shown read-only rather than borrowing the open folder's permission.
   */
  canEditIn?: (folderId: string | null) => boolean;
  /** Needed only where editing is offered: the most text one save may carry. */
  me?: SpaceSummary;
  /** The file's content or versions changed: its folder's listing and the quota are stale. */
  onChanged?: (folderId: string | null) => void;
  onCopyPath: (address: string) => void;
  /** Each time the file is read, the first time and every re-read while it is being indexed. */
  onLoaded?: (file: SpaceFileDetail) => void;
  /** Whether an edit is unsaved, for a frame that asks before it closes. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  // The callbacks are read through refs: a frame passing inline functions must not re-create `loadFile`,
  // which would re-run the effect that loads the file on every render.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  const [file, setFile] = useState<SpaceFileDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState<SpaceText | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  const [inlineUrl, setInlineUrl] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The save was refused because a newer version exists — the one state where reloading is the answer. */
  const [stale, setStale] = useState(false);

  const loadFile = useCallback(async () => {
    try {
      const next = await spacesApi.file(fileId);
      setFile(next);
      setError(null);
      onLoadedRef.current?.(next);
      return next;
    } catch (err) {
      setError(spaceErrorMessage(err, 'Could not open this file'));
      return null;
    }
  }, [fileId]);

  const loadPreview = useCallback(async (detail: SpaceFileDetail) => {
    const kind = previewKindOf(detail.mimeType, detail.name);
    setText(null);
    setTextError(null);
    setInlineUrl(null);
    setInlineError(null);
    if (detail.status === 'PENDING' || kind === 'none') return null;
    // A document's text does not exist until it has been read; the panel asks again once it is.
    if (kind === 'extracted' && (detail.indexStatus === 'PENDING' || detail.indexStatus === 'NONE')) return null;
    setPreviewLoading(true);
    try {
      if (kind === 'markdown' || kind === 'text' || kind === 'extracted') {
        const next = await spacesApi.text(detail.id);
        setText(next);
        return next;
      }
      const signed = await spacesApi.download(detail.id, { inline: true });
      setInlineUrl(signed.url);
    } catch (err) {
      const message = spaceErrorMessage(err, 'Could not load a preview');
      if (kind === 'markdown' || kind === 'text' || kind === 'extracted') setTextError(message);
      else setInlineError(message);
    } finally {
      setPreviewLoading(false);
    }
    return null;
  }, []);

  useEffect(() => {
    setFile(null);
    setEditing(false);
    setStale(false);
    setSaveError(null);
    setActionError(null);
    void loadFile().then((detail) => detail && loadPreview(detail));
  }, [loadFile, loadPreview]);

  // Re-read while the file is being indexed, so its search status — and a document's text — appear on their own.
  const refreshFile = useCallback(() => void loadFile(), [loadFile]);
  // …and while an archive is being unpacked, so its count climbs and its outcome appears.
  const unpackingNow = file?.unpacking?.status === 'QUEUED' || file?.unpacking?.status === 'RUNNING';
  useRefreshWhileIndexing(file?.indexStatus === 'PENDING' || file?.indexStatus === 'NONE' || unpackingNow, refreshFile, fileId);
  const lastStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const was = lastStatus.current;
    lastStatus.current = file?.indexStatus;
    if (file && (was === 'PENDING' || was === 'NONE') && file.indexStatus !== was && previewKindOf(file.mimeType, file.name) === 'extracted') {
      void loadPreview(file);
    }
  }, [file, loadPreview]);

  const kind = file ? previewKindOf(file.mimeType, file.name) : 'none';
  const editable =
    file !== null && me !== undefined && canEditIn?.(file.parentId) === true && text !== null && (kind === 'markdown' || kind === 'text');
  const dirty = editing && text !== null && draft !== text.content;
  useEffect(() => onDirtyChangeRef.current?.(dirty), [dirty]);

  async function save() {
    if (!file || !text || !me) return;
    const bytes = new TextEncoder().encode(draft).length;
    if (bytes > me.maxTextWriteBytes) {
      setSaveError(`That is ${formatBytes(bytes)}; text edits are limited to ${formatBytes(me.maxTextWriteBytes)}. Upload it as a file instead.`);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // `ifVersion` is the version this draft was made from: if someone else saved since, the server
      // refuses instead of silently writing over their change.
      await spacesApi.writeContent(file.id, { content: draft, ifVersion: text.versionId });
      setEditing(false);
      const detail = await loadFile();
      if (detail) await loadPreview(detail);
      onChanged?.(file.parentId);
    } catch (err) {
      if (isConflict(err)) setStale(true);
      setSaveError(spaceErrorMessage(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  }

  async function reloadLatest() {
    const detail = await loadFile();
    if (!detail) return;
    const latest = await loadPreview(detail);
    if (latest) setDraft(latest.content);
    setStale(false);
    setSaveError(null);
  }

  async function download(version?: string) {
    setActionError(null);
    try {
      await downloadFile(fileId, version);
    } catch (err) {
      setActionError(spaceErrorMessage(err, 'Could not download'));
    }
  }

  return error ? (
    <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">{error}</p>
  ) : !file ? (
    <p className="text-xs text-ink-faint">Loading…</p>
  ) : (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="primary" onClick={() => void download()} disabled={file.status === 'PENDING'}>
          <Download size={12} />
          Download
        </Button>
        <Button size="sm" onClick={() => onCopyPath(file.address)} title="Copy the path Claude uses for this file">
          <Copy size={12} />
          Copy MCP path
        </Button>
        {editable && !editing && (
          <Button
            size="sm"
            onClick={() => {
              setDraft(text?.content ?? '');
              setEditing(true);
              setStale(false);
              setSaveError(null);
            }}
          >
            <Pencil size={12} />
            Edit
          </Button>
        )}
      </div>
      {actionError && <p className="text-xs text-danger-text">{actionError}</p>}

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
        <Meta label="Location"><span className="font-mono">{file.address}</span></Meta>
        <Meta label="Type">{file.mimeType ?? '—'}</Meta>
        <Meta label="Size">{formatBytes(file.sizeBytes)}</Meta>
        <Meta label="Modified">
          {formatDate(file.updatedAt)}
          {file.modifiedBy ? ` by ${file.modifiedBy}` : ''}
        </Meta>
        {file.indexStatus && file.indexStatus !== 'NONE' && !file.unpacking && (
          <Meta label="Search">
            {INDEX_STATUS_LABEL[file.indexStatus]}
            {file.indexError && file.indexStatus !== 'INDEXED' && <span className="block text-[10px] text-ink-faint">{file.indexError}</span>}
          </Meta>
        )}
        {file.unpacking && (
          <Meta label="Unpacked">
            {unpackLabel(file.unpacking)}
            {file.unpacking.into && file.unpacking.status !== 'SUPERSEDED' && (
              <span className="block font-mono text-[11px]">{file.unpacking.into}</span>
            )}
            {file.unpacking.error && file.unpacking.status !== 'DONE' && <span className="block text-[10px] text-ink-faint">{file.unpacking.error}</span>}
          </Meta>
        )}
      </dl>

      <section>
        <h3 className="text-[11px] font-semibold text-ink-faint uppercase tracking-wide mb-2">{editing ? 'Editing' : 'Preview'}</h3>
        {editing ? (
          <div className="space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={kind === 'markdown'}
              className="font-mono text-xs min-h-[50vh] resize-y"
            />
            {saveError && (
              <div className={cn('rounded-md border px-3 py-2 text-xs', stale ? 'border-warning-ring bg-warning-bg text-warning-text' : 'border-danger-ring bg-danger-bg text-danger-text')}>
                {stale
                  ? 'Someone saved a newer version of this file while you were editing. Reload to see it — copy your changes first if you want to keep them.'
                  : saveError}
                {stale && (
                  <button type="button" onClick={() => void reloadLatest()} className="ml-2 inline-flex items-center gap-1 font-medium underline">
                    <RefreshCw size={11} />
                    Reload
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button size="sm" variant="primary" onClick={() => void save()} disabled={saving || stale || !dirty}>
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (dirty && !window.confirm('Discard your unsaved changes?')) return;
                  setEditing(false);
                  setStale(false);
                  setSaveError(null);
                }}
              >
                Cancel
              </Button>
              <span className="text-[10px] text-ink-faint">Saving makes a new version; the old one stays in the history below.</span>
            </div>
          </div>
        ) : (
          <Preview file={file} text={text} textError={textError} inlineUrl={inlineUrl} inlineError={inlineError} loading={previewLoading} />
        )}
      </section>

      <section>
        <h3 className="text-[11px] font-semibold text-ink-faint uppercase tracking-wide mb-2">Versions</h3>
        {file.versions.length === 0 ? (
          <p className="text-xs text-ink-faint">No versions yet.</p>
        ) : (
          <ul className="rounded-md border border-border divide-y divide-border-subtle">
            {file.versions.map((version) => (
              <li key={version.id} className="flex items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-ink flex items-center gap-1.5">
                    {formatDate(version.createdAt)}
                    {version.current && <Badge variant="success" className="px-1.5 py-0 text-[10px]">current</Badge>}
                    <Badge variant="muted" className="px-1.5 py-0 text-[10px]">{version.createdVia === 'mcp' ? 'MCP' : 'console'}</Badge>
                  </p>
                  <p className="text-[10px] text-ink-faint truncate">
                    {formatBytes(version.sizeBytes)} · {version.createdByEmail ?? 'unknown'}
                    {version.mimeType !== file.mimeType ? ` · ${version.mimeType}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void download(version.id)}
                  title="Download this version"
                  className="p-1.5 rounded text-ink-faint hover:text-ink hover:bg-surface-muted transition-colors"
                >
                  <Download size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export function FilePanel({
  fileId, canEditIn, me, onClose, onChanged, onCopyPath,
}: {
  fileId: string;
  canEditIn: (folderId: string | null) => boolean;
  me: SpaceSummary;
  onClose: () => void;
  onChanged: (folderId: string | null) => void;
  onCopyPath: (address: string) => void;
}) {
  const [title, setTitle] = useState<string | null>(null);
  const dirty = useRef(false);
  useEffect(() => setTitle(null), [fileId]);

  function close() {
    if (dirty.current && !window.confirm('Discard your unsaved changes?')) return;
    onClose();
  }

  return (
    <Drawer open onClose={close} title={title ?? 'File'} className="max-w-2xl">
      <FileDetail
        fileId={fileId}
        canEditIn={canEditIn}
        me={me}
        onChanged={onChanged}
        onCopyPath={onCopyPath}
        onLoaded={(file) => setTitle(file.name)}
        onDirtyChange={(next) => {
          dirty.current = next;
        }}
      />
    </Drawer>
  );
}
